import * as Dfns from 'date-fns/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  MenuItem,
  OfflineInputTabs,
  TimelineEntry
} from '@blisspointmedia/bpm-types/dist/OfflineInputs';
import { SetError } from '../../../redux/modals';
import * as UserRedux from '../../../redux/user';
import { DateRange } from '../../../utils/date-utils';
import { AddPromoDataModal } from './AddPromoDataModal';
import { ImportPromoDataModal } from './ImportPromoDataModal';
import { InputContainer } from './InputContainer';
import { CommitSuccessDialog, DiscardConfirmationDialog } from './OfflineInputsDialogs';
import {
  commitPromosChanges,
  OrderByOptions,
  promoDownload,
  stageEntry
} from './OfflineInputsUtils';
import { TimelineBarChart } from './TimelineBarChart';

interface PromoDataProps {
  inputType: keyof typeof OfflineInputTabs;
  promos: MenuItem[];
  setPromos: (promos: MenuItem[]) => void;
  offers: MenuItem[];
  setOffers: (offers: MenuItem[]) => void;
  timelineEntries: TimelineEntry[];
  stagedEntries: TimelineEntry[];
  setStagedEntries: React.Dispatch<React.SetStateAction<TimelineEntry[]>>;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  updated: { by?: string; at?: string };
  fetching: boolean;
  fetchFailed: boolean;
  refetch: () => void;
  editingUnlocked: boolean;
  setEditingUnlocked: React.Dispatch<React.SetStateAction<boolean>>;
  dispatchStateReset: boolean;
  setDispatchStateReset: React.Dispatch<React.SetStateAction<boolean>>;
  company: string;
  setError: SetError;
}

const WIDTH_PER_MONTH = 800 / 12;
const MIN_HEIGHT_PER_ENTRY = 44;

export const Promos = ({
  inputType,
  promos,
  setPromos,
  offers,
  setOffers,
  timelineEntries,
  stagedEntries,
  setStagedEntries,
  hasUnsavedChanges,
  setHasUnsavedChanges,
  updated,
  fetching,
  fetchFailed,
  refetch,
  editingUnlocked,
  setEditingUnlocked,
  dispatchStateReset,
  setDispatchStateReset,
  company,
  setError
}: PromoDataProps) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [stagedDeletes, setStagedDeletes] = useState<TimelineEntry[]>([]);
  const [showAddPromoDataModal, setShowAddPromoDataModal] = useState(false);
  const [showImportPromoDataModal, setShowImportPromoDataModal] = useState(false);
  const [showCommitSuccessDialog, setShowCommitSuccessDialog] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const userFullName: string = useSelector(UserRedux.fullNameSelector) ?? '';
  const [orderBy, setOrderBy] = useState<OrderByOptions>(OrderByOptions.DATE_ASCENDING);
  const [numVisibleEntries, setNumVisibleEntries] = useState<number>(0);
  const [timelineDateRange, setTimelineDateRange] = useState<DateRange>({
    start: Dfns.format('yyyy-MM-dd')(Dfns.sub({ months: 5 })(Dfns.startOfMonth(new Date()))),
    end: Dfns.format('yyyy-MM-dd')(Dfns.add({ months: 6 })(Dfns.endOfMonth(new Date())))
  });

  const resetState = useCallback(() => {
    setStagedEntries([]);
    setStagedDeletes([]);
    setSubmitting(false);
    setEditingUnlocked(false);
    setHasUnsavedChanges(false);
    setShowAddPromoDataModal(false);
    setShowCommitSuccessDialog(false);
    setShowDiscardDialog(false);
  }, [setStagedEntries, setEditingUnlocked, setHasUnsavedChanges]);

  useEffect(() => {
    if (dispatchStateReset) {
      resetState();
      setDispatchStateReset(false);
    }
  }, [dispatchStateReset, resetState, setDispatchStateReset]);

  useEffect(() => {
    setHasUnsavedChanges(Boolean(stagedEntries.length) || Boolean(stagedDeletes.length));
  }, [stagedEntries, stagedDeletes, setHasUnsavedChanges]);

  const onCommitSuccess = () => {
    refetch();
    resetState();
    setShowCommitSuccessDialog(true);
  };

  const validateAndUpdateTables = async () => {
    setSubmitting(true);
    await commitPromosChanges(stagedEntries, stagedDeletes, onCommitSuccess, setError, company);
    setSubmitting(false);
  };

  const allActiveEntries = useMemo(
    () =>
      [...timelineEntries, ...stagedEntries].filter(
        addedEntry =>
          !stagedDeletes.some(
            deletedEntry => deletedEntry.promoPeriodId === addedEntry.promoPeriodId
          )
      ),
    [timelineEntries, stagedEntries, stagedDeletes]
  );

  const timelineChartHeight = useMemo(() => {
    const minChartHeight = numVisibleEntries * MIN_HEIGHT_PER_ENTRY;
    return { height: `max(${minChartHeight}px, 100%)` };
  }, [numVisibleEntries]);

  // date ranges < 1 year will expand to size of container.
  // date ranges > 1 year will be scrollable, extending width based 800 px /year baseline
  const timelineChartWidth = useMemo(() => {
    const numDisplayedMonths =
      Dfns.differenceInCalendarMonths(Dfns.parseISO(timelineDateRange.start))(
        Dfns.parseISO(timelineDateRange.end)
      ) + 1;
    const additionalPixels =
      numDisplayedMonths > 12 ? (numDisplayedMonths - 12) * WIDTH_PER_MONTH : 0;
    return { width: `calc(100% + ${additionalPixels}px)` };
  }, [timelineDateRange]);

  const download = () => promoDownload(allActiveEntries, `${company}_promotion_timeline`);

  const pushStagedDelete = useCallback(
    (newDelete: TimelineEntry) => {
      setStagedDeletes([...stagedDeletes, newDelete]);
    },
    [setStagedDeletes, stagedDeletes]
  );

  return (
    <InputContainer
      dialogs={
        <>
          <AddPromoDataModal
            show={showAddPromoDataModal}
            handleClose={() => setShowAddPromoDataModal(false)}
            stageEntry={input => stageEntry(input, setStagedEntries)}
            promos={promos}
            setPromos={setPromos}
            offers={offers}
            setOffers={setOffers}
            userFullName={userFullName}
          />
          <CommitSuccessDialog
            show={showCommitSuccessDialog}
            onHide={() => setShowCommitSuccessDialog(false)}
          />
          <DiscardConfirmationDialog
            show={showDiscardDialog}
            onDiscard={() => resetState()}
            onHide={() => setShowDiscardDialog(false)}
          />
          <ImportPromoDataModal
            show={showImportPromoDataModal}
            promos={promos}
            offers={offers}
            userFullName={userFullName}
            handleClose={() => setShowImportPromoDataModal(false)}
            stageEntry={input => stageEntry(input, setStagedEntries)}
            setPromos={setPromos}
            setOffers={setOffers}
            setEditingUnlocked={setEditingUnlocked}
            setError={setError}
          />
        </>
      }
      tables={
        <div
          className='tables-container-inner timeline-chart'
          style={{ ...timelineChartWidth, ...timelineChartHeight }}
        >
          <TimelineBarChart
            editingUnlocked={editingUnlocked}
            pushStagedDelete={pushStagedDelete}
            timelineEntries={allActiveEntries}
            setNumVisibleEntries={setNumVisibleEntries}
            timelineDateRange={timelineDateRange}
            orderBy={orderBy}
          />
        </div>
      }
      inputType={inputType}
      updated={updated}
      isEmpty={timelineEntries.length + stagedEntries.length === 0}
      fetching={fetching}
      fetchFailed={fetchFailed}
      refetch={refetch}
      submitting={submitting}
      editingUnlocked={editingUnlocked}
      setEditingUnlocked={setEditingUnlocked}
      hasUnsavedChanges={hasUnsavedChanges}
      validateAndUpdateTables={validateAndUpdateTables}
      setShowAddInputModal={setShowAddPromoDataModal}
      setShowDiscardDialog={setShowDiscardDialog}
      setError={setError}
      download={download}
      orderBy={orderBy}
      setOrderBy={setOrderBy}
      timelineDateRange={timelineDateRange}
      setTimelineDateRange={setTimelineDateRange}
      setShowImportModal={setShowImportPromoDataModal}
    />
  );
};
